// ============================================================
// VENOM MAP — Export Utilities
// Generates JSON, CSV, and HTML report from scan result objects.
// No external dependencies — pure client-side.
// ============================================================

/**
 * Trigger a browser file download.
 * @param {string} content - File content
 * @param {string} filename - Download filename
 * @param {string} mime - MIME type
 */
function triggerDownload(content, filename, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Export a full scan result as a JSON file.
 * Includes all fields: scanners, findings, risk scoring formula, geo, etc.
 */
export function exportScanAsJson(result) {
  const ts = new Date().toISOString().replace(/[:.]/g, '-');
  const filename = `venom-map_${result.domain}_${ts}.json`;
  triggerDownload(JSON.stringify(result, null, 2), filename, 'application/json');
}

/**
 * Export a scan result as a flat CSV file.
 * One row per finding: domain, scanner_code, scanner_name, scanner_score, severity, message.
 */
export function exportScanAsCsv(result) {
  const rows = [
    ['domain', 'scan_id', 'threat_level', 'overall_risk', 'scanner_code', 'scanner_name',
     'scanner_score', 'finding_severity', 'finding_message']
  ];

  const scanners = result.details?.scanners || [];
  for (const s of scanners) {
    if (s.findings.length === 0) {
      rows.push([
        result.domain, result.id, result.threatLevel, result.overallRisk,
        s.code, s.name, s.score, '', 'No findings'
      ]);
    } else {
      for (const f of s.findings) {
        rows.push([
          result.domain, result.id, result.threatLevel, result.overallRisk,
          s.code, s.name, s.score, f.severity, f.message
        ]);
      }
    }
  }

  const csvContent = rows.map(r =>
    r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
  ).join('\n');

  const ts = new Date().toISOString().replace(/[:.]/g, '-');
  const filename = `venom-map_${result.domain}_${ts}.csv`;
  triggerDownload(csvContent, filename, 'text/csv;charset=utf-8;');
}

/**
 * Export a scan result as a standalone printable HTML report.
 * The output is a self-contained file (no external deps) styled like a terminal report.
 * Suitable for printing as PDF via the browser.
 */
export function exportScanAsHtmlReport(result) {
  const scanDate = new Date().toLocaleString();
  const riskColor = getRiskColorHtml(result.threatLevel);
  const scanners = result.details?.scanners || [];
  const risk = result.details?.risk;

  // Scanner sections
  const scannerSections = scanners.map(s => {
    const scoreColor = getRiskColorHtml(getLevel(s.score));
    const findingsRows = s.findings.length > 0
      ? s.findings.map(f => `
          <tr>
            <td class="sev sev-${f.severity}">${f.severity}</td>
            <td>${htmlEsc(f.message)}</td>
          </tr>`).join('')
      : '<tr><td colspan="2" class="no-findings">✓ No findings detected</td></tr>';

    return `
      <section class="scanner-block">
        <div class="scanner-head">
          <span class="s-code">${s.code}</span>
          <span class="s-name">${htmlEsc(s.name)}</span>
          <span class="s-score" style="color:${scoreColor}">${s.score}/100</span>
        </div>
        <table class="findings-table">
          <thead><tr><th>Severity</th><th>Finding</th></tr></thead>
          <tbody>${findingsRows}</tbody>
        </table>
      </section>`;
  }).join('');

  // Score formula
  let formulaSection = '';
  if (risk?.scoreFormula?.length > 0) {
    const formulaRows = risk.scoreFormula.map(f => {
      const c = getRiskColorHtml(getLevel(f.score));
      return `<tr>
        <td class="f-code">${f.code}</td>
        <td>${htmlEsc(f.name)}</td>
        <td style="color:${c};font-weight:700">${f.score}/100</td>
        <td>${Math.round(f.weight * 100)}%</td>
        <td style="color:#a78bfa">${f.contribution}</td>
      </tr>`;
    }).join('');
    const totalW = risk.scoreFormula.reduce((s, f) => s + f.weight, 0);
    const sumC = risk.scoreFormula.reduce((s, f) => s + f.contribution, 0);
    formulaSection = `
      <section class="formula-block">
        <h2>⚖️ Score Formula Audit</h2>
        <p class="formula-note">Overall Risk = Σ(score × weight) ÷ Σ(weights)</p>
        <table class="formula-table">
          <thead><tr><th>Code</th><th>Scanner</th><th>Score</th><th>Weight</th><th>Contribution</th></tr></thead>
          <tbody>${formulaRows}</tbody>
          <tfoot>
            <tr><td colspan="3">Σ weighted = <strong>${sumC.toFixed(1)}</strong></td>
                <td><strong>${totalW.toFixed(2)}</strong></td>
                <td style="color:${riskColor};font-weight:800">${result.overallRisk}/100</td></tr>
          </tfoot>
        </table>
      </section>`;
  }

  // Detected tools
  let toolsSection = '';
  if (risk?.detectedTools?.length > 0) {
    const toolRows = risk.detectedTools.map(t => {
      const confColor = t.confidence === 'HIGH' ? '#44dd66' : t.confidence === 'MEDIUM' ? '#ffaa22' : '#666';
      return `
        <div class="tool-card">
          <div class="tool-head">
            <span class="tool-label">${htmlEsc(t.label)}</span>
            <span class="tool-conf" style="color:${confColor}">${t.confidence || '?'} CONFIDENCE</span>
          </div>
          <div class="tool-body">
            <p><span class="k">Triggered by:</span> ${htmlEsc(t.triggerReason || '—')}</p>
            <p><span class="k">Scanner:</span> ${t.triggerScanner || '—'} &nbsp;|&nbsp;
               <span class="k">Score at trigger:</span> ${t.triggerScore ?? '—'}/100</p>
            ${t.falsePositiveWarning ? `<p class="fp-warn">⚠️ ${htmlEsc(t.falsePositiveWarning)}</p>` : ''}
          </div>
        </div>`;
    }).join('');
    toolsSection = `<section class="tools-block"><h2>🔎 Detected Tools — Reasoning Trace</h2>${toolRows}</section>`;
  }

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <title>VENOM MAP — Scan Report: ${htmlEsc(result.domain)}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:'JetBrains Mono',monospace;background:#060810;color:#e0e8f0;padding:2rem;font-size:13px}
    h1{font-size:1.4rem;color:#00ff88;letter-spacing:3px;margin-bottom:0.3rem}
    h2{font-size:0.75rem;color:#6b7b8d;letter-spacing:2px;text-transform:uppercase;margin:1.5rem 0 0.6rem;padding-bottom:0.3rem;border-bottom:1px solid rgba(255,255,255,0.08)}
    .meta{color:#3a4a5c;font-size:0.7rem;margin-bottom:1.5rem}
    .summary-box{border:1px solid;border-radius:8px;padding:1rem 1.5rem;margin-bottom:1.5rem;display:flex;align-items:center;gap:2rem}
    .risk-num{font-size:3rem;font-weight:900;line-height:1}
    .threat{font-size:0.8rem;font-weight:700;padding:0.3rem 0.8rem;border-radius:4px;letter-spacing:1px}
    .domain-info{color:#6b7b8d;font-size:0.75rem}
    .scanner-block{margin-bottom:1rem;border:1px solid rgba(255,255,255,0.07);border-radius:6px;overflow:hidden}
    .scanner-head{display:flex;align-items:center;gap:0.8rem;padding:0.6rem 0.8rem;background:rgba(255,255,255,0.02)}
    .s-code{background:rgba(0,255,136,0.1);color:#00ff88;padding:0.1rem 0.4rem;border-radius:3px;font-size:0.65rem;font-weight:700}
    .s-name{flex:1;font-size:0.75rem}
    .s-score{font-weight:700;font-size:0.8rem}
    .findings-table{width:100%;border-collapse:collapse}
    .findings-table th{font-size:0.6rem;color:#3a4a5c;padding:0.4rem 0.8rem;text-align:left;background:rgba(0,0,0,0.2);letter-spacing:1px}
    .findings-table td{font-size:0.7rem;padding:0.35rem 0.8rem;border-top:1px solid rgba(255,255,255,0.04);color:#6b7b8d;word-break:break-word}
    .no-findings{color:#3a4a5c;font-style:italic}
    .sev{font-size:0.58rem;font-weight:700;padding:0.1rem 0.3rem;border-radius:2px;white-space:nowrap}
    .sev-CRITICAL{background:rgba(255,34,85,.15);color:#ff2255}
    .sev-HIGH{background:rgba(255,102,68,.15);color:#ff6644}
    .sev-MEDIUM{background:rgba(255,170,34,.12);color:#ffaa22}
    .sev-LOW{background:rgba(68,221,102,.12);color:#44dd66}
    .sev-INFO{background:rgba(34,204,255,.1);color:#22ccff}
    .formula-block,.tools-block{margin-bottom:1.5rem}
    .formula-note{color:#3a4a5c;font-size:0.68rem;font-style:italic;margin-bottom:0.6rem}
    .formula-table{width:100%;border-collapse:collapse}
    .formula-table th{font-size:0.6rem;color:#3a4a5c;padding:0.4rem 0.6rem;text-align:left;letter-spacing:1px}
    .formula-table td{font-size:0.7rem;padding:0.35rem 0.6rem;border-top:1px solid rgba(255,255,255,0.04)}
    .formula-table tfoot td{border-top:1px solid rgba(167,139,250,.3);color:#6b7b8d;font-size:0.7rem}
    .f-code{color:#00ff88;font-weight:700}
    .tool-card{border:1px solid rgba(255,255,255,0.08);border-radius:6px;margin-bottom:0.6rem;overflow:hidden}
    .tool-head{display:flex;justify-content:space-between;padding:0.5rem 0.8rem;background:rgba(255,255,255,0.02)}
    .tool-label{font-weight:700;font-size:0.78rem}
    .tool-conf{font-size:0.6rem;font-weight:700;letter-spacing:1px}
    .tool-body{padding:0.5rem 0.8rem;border-top:1px solid rgba(255,255,255,0.05);font-size:0.7rem;color:#6b7b8d;line-height:1.8}
    .k{color:#3a4a5c}
    .fp-warn{color:#ffaa22;margin-top:0.3rem}
    .footer{margin-top:2rem;padding-top:1rem;border-top:1px solid rgba(255,255,255,0.06);color:#3a4a5c;font-size:0.65rem;text-align:center}
    @media print{body{background:#fff;color:#000}h1,h2{color:#000}.sev-CRITICAL{color:#c0003a}.meta,footer{color:#666}}
  </style>
</head>
<body>
  <h1>⚗️ VENOM MAP — Scan Report</h1>
  <div class="meta">Generated: ${scanDate} &nbsp;|&nbsp; Scan ID: ${result.id} &nbsp;|&nbsp; Duration: ${(result.scanDuration/1000).toFixed(1)}s</div>

  <div class="summary-box" style="border-color:${riskColor}20;background:${riskColor}08">
    <div class="risk-num" style="color:${riskColor}">${result.overallRisk}</div>
    <div>
      <div class="threat" style="background:${riskColor}20;color:${riskColor};border:1px solid ${riskColor}40">${result.threatLevel}</div>
      <div class="domain-info" style="margin-top:0.5rem">
        ${htmlEsc(result.domain)} &nbsp;•&nbsp; ${htmlEsc(result.country || 'Unknown')} &nbsp;•&nbsp; ${htmlEsc(result.ip || 'N/A')}
      </div>
    </div>
  </div>

  ${formulaSection}
  ${toolsSection}
  <h2>Scanner Results</h2>
  ${scannerSections}

  <div class="footer">
    VENOM MAP — Web Data Poisoning Scanner &nbsp;|&nbsp; by Fabrizio Degni &nbsp;|&nbsp; © 2026 &nbsp;|&nbsp; Educational use only
  </div>
</body>
</html>`;

  const ts = new Date().toISOString().replace(/[:.]/g, '-');
  const filename = `venom-map-report_${result.domain}_${ts}.html`;
  triggerDownload(html, filename, 'text/html;charset=utf-8;');
}

/**
 * Export the scan history summary as CSV.
 * @param {Array} scans - Array of history scan objects
 */
export function exportHistoryAsCsv(scans) {
  const rows = [['id', 'domain', 'overall_risk', 'threat_level', 'country', 'ip', 'scan_duration_ms', 'created_at']];
  for (const s of scans) {
    rows.push([s.id, s.domain, s.overallRisk, s.threatLevel, s.country || '', s.ip || '', s.scanDuration || '', s.createdAt || '']);
  }
  const csvContent = rows.map(r =>
    r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
  ).join('\n');

  const ts = new Date().toISOString().replace(/[:.]/g, '-');
  triggerDownload(csvContent, `venom-map-history-${ts}.csv`, 'text/csv;charset=utf-8;');
}

// --- Internal helpers ---
function getRiskColorHtml(level) {
  const colors = { CRITICAL: '#ff2255', HIGH: '#ff6644', MEDIUM: '#ffaa22', LOW: '#44dd66', CLEAN: '#22ccff' };
  return colors[level] || '#666';
}
function getLevel(score) {
  if (score >= 75) return 'CRITICAL';
  if (score >= 50) return 'HIGH';
  if (score >= 25) return 'MEDIUM';
  if (score >= 10) return 'LOW';
  return 'CLEAN';
}
function htmlEsc(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
